import React, { useState, useMemo, useRef, useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCSwitch, RCCTooltip, RCCLoaderBackdropComponent, RCCIcon  } from "react-common-components";
import { useForm } from "react-hook-form";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import ConfigsContainer from "src/components/ConfigsContainer";
import Title from "src/components/Title";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import ButtonScrollTo from "src/components/ButtonScrollTo";
import LovSubsidiary from "src/components/Lov/Subsidiary";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import DateTimePicker from "src/components/DateTimePicker";
import ModalIndicatorChart from "src/pages/Indicators/Panel/components/ModalIndicatorChart";
import { IRefActionsModalChartIndicators } from "src/pages/Indicators/Panel/components/ModalIndicatorChart/interface";
import downloadFile from "src/utils/downloadFile";
import ExportListContainer from "src/components/List/exportList";
import { IRefExportListComponent } from "src/components/List/interface";
import { endOfMonth, startOfMonth, subDays, subMonths } from "date-fns";
import { useHistory } from "react-router-dom";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import stringToDate from "src/utils/stringToDate";
import LovCoins from "src/components/Lov/Coins";
import useDragHorizontal from "src/hooks/useDragHorizontal";
import { IComponentConfigsIncomeStatement, IConfigsIncomeStatement, IDataTable, IUrlMemorizedParams, IValuesTable } from "./interface";
import TableOfIncomeValues from "./tableOfIncomeValues";
import LovIncomeStatementWithOptions from "./components/LovIncomeStatementWithOptions";
import * as ListStyles from "./components/List/styles";

const IncomeStatement: React.FC<IComponentConfigsIncomeStatement> = ({
	budgetPlanId,
	budgetPlanTitle,
	configs: ParamsConfigs,
	subsidiaryDefaultValue,
	subsidiaryDefaultLabel
}) => {
	const divRef = useRef<HTMLDivElement>(null);
	const { onMouseDown } = useDragHorizontal(divRef, true);
	const { confirm } = useConfirmModal();
	const history = useHistory();
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const [loadingBackdrop, setLoadingBackdrop] = useState(false);
	const [subsidiary, setSubsidiary] = useState<{
		title: string;
		id: number;
	} | undefined>(undefined);
	
	const baseVerticalValues = useRef<IValuesTable[]>();
	const valuesOfQuantity = useRef<IValuesTable[]>();
	const memorizedUrlParams = useRef<IUrlMemorizedParams>({} as IUrlMemorizedParams);
	const modalChartIndicatorsValues = useRef<IRefActionsModalChartIndicators>(null);
	const [showConfigs, setShowConfigs] = useState(false);
	const [showDtForecast, setShowDtForecast] = useState(ParamsConfigs.forecast === "S");
	const [configs, setConfigs] = useState<IConfigsIncomeStatement>(ParamsConfigs);
	const { register : registerConfigs, control: controlConfigs } = useForm();

	const [loadingExport, setLoadingExport] = useState(false);
	const exportListContainerRef = useRef<IRefExportListComponent>(null);
	const exportStructure = async () => {
		const data = exportListContainerRef.current?.executeExport();

		if(!data || data.length === 0){
			addToast({
				title: translate("Erro"),
				description: translate("Não há valores para exportar"),
				color: "yellow"
			});
			return;	
		}

		try{	
			setLoadingExport(true);

			downloadFile(data.join("\n"), `${translate("Demonstrativo resultado do exercício")} - ${budgetPlanTitle}.csv`, true);

			addToast({
				color: "green",
				description: translate("Estrutura exportada com sucesso"),
				title: translate("Sucesso")
			});

			setLoadingExport(false);
		}catch(err){
			setLoadingExport(false);
			standardErrorMessages(err, translate("Erro ao exportar estrutura valores"), addToast);
		}
	};


	const { handleSubmit : handleSubmitFormOptions, control: controlFormOptions, setValue: setValueFormOptions } = useForm();
	const submitFormOptions = async (data: any) => {
		const dataForm = {
			...data,
			subsidiary: typeof data.subsidiary === "object" ? data.subsidiary.value : data.subsidiary
		};	
		loadData({ ...configs, ...dataForm});
	};

	const [dataTable, setDataTable] = useState<IDataTable[]>([]);
	const [loading, setLoading] = useState(false);
	const [errorLoadingData, setErrorLoadingData] = useState(false);
	const [hasConversionValueEqualZero, setHasConversionValueEqualZero] = useState(false);
	const loadData = async (dataForm: any) => {
		if(!budgetPlanId)
			return;
			
		try{
			setLoading(true);
			setErrorLoadingData(false);

			const urlParams = {
				ofa001Id: budgetPlanId,
				ofm001Id: dataForm.ofm001Id,
				subsidiary: dataForm.subsidiary || "",
				startDate: dataForm.startDate ? formatDate(dataForm.startDate) : "",
				endDate: dataForm.endDate ? formatDate(dataForm.endDate) : "",
				divider: dataForm.divider,
				forecast: dataForm.forecast === "S",
				showColumnLastYear: dataForm.showColumnLastYear === "S",
				showColumnPlanned: dataForm.forecast === "S" || dataForm.showColumnPlanned === "S",
				showColumnRealized: dataForm.forecast === "S" || dataForm.showColumnRealized === "S",
				showVariationValue: dataForm.showVariationValue === "S",
				showVariationPercent: dataForm.showVariationPercent === "S",
				showHorizontalAnalysis: dataForm.showHorizontalAnalysis === "S",
				showQuantity: dataForm.showQuantity === "S",
				showVerticalAnalysis: dataForm.showVerticalAnalysis === "S",
				showCorporativeValues: dataForm.corporativeValues === "S",
				dtForecast: dataForm.dtForecast,
				variation: dataForm.variation,
				showPeriods: dataForm.showPeriods === "T",
				updateIndicators: dataForm.updateIndicators === "S",
				corporativeValues: dataForm.corporativeValues === "S",
				coinPlanned: coinConversion && dataForm.coinPlanned && dataForm.coinPlanned !== null ? dataForm.coinPlanned.toString() : "",
				coinRealized: coinConversion && dataForm.coinRealized && dataForm.coinRealized !== null ? dataForm.coinRealized.toString() : ""
			} as IUrlMemorizedParams;

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/IncomeStatement",
				urlParameters: {
					...urlParams
				}
			});
			memorizedUrlParams.current = urlParams;
			setConfigs((prevState) => ({
				...prevState,
				startDate: dataForm.startDate || new Date(new Date(new Date().getFullYear(), 0, 1)),
				endDate: dataForm.endDate || new Date(new Date(new Date().getFullYear(), 11, 31))
			}));
			setHasConversionValueEqualZero(data.hasConversionValueEqualZero);
			setDataTable(data.list);
			baseVerticalValues.current = data.baseVerticalAnalysis;
			valuesOfQuantity.current = data.valuesOfQuantity;
			setLoading(false);
		}catch(err){
			setErrorLoadingData(true);
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoading(false);
		}
	};

	const [corporativeValueOutdated, setCorporativeValueOutdated] = useState(false);
	const [coinConversion, setCoinConversion] = useState(false);
	const fetchIfBudgetUsesApportionment = async () => {
		if(!budgetPlanId)
			return;
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetConfig",
				urlParameters: {
					budgetId: budgetPlanId,
					origin: "5"
				}
			});
			setCorporativeValueOutdated(data.budget.off001Usarateiocc === "S" && data.budgetPlan?.ofa001Vlrcorpatu === "N");
			setCoinConversion(data.budgetPlan?.ofa001Utilizamoeda === "S");
		}catch(err){
			setCorporativeValueOutdated(false);
		}
	};
	const updateCorporativeValues = () => {
		confirm(
			translate("Atualizar valores corporativos"), 
			`${translate("Esse processo pode demorar alguns minutos, deseja continuar")}?`,
			async () => {
				if(!budgetPlanId)
					return;

				await RCCServicesApi.putRequest({
					method: "FinancialManagement/UpdateCorporativeValues",
					urlParameters: {
						budgetPlanId
					}
				});

				setCorporativeValueOutdated(false);

				addToast({
					color: "green",
					description: translate("Valores corporativos atualizados com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores corporativos"), addToast);
			},
			false
		);
	};

	const Table = useMemo(() => {
		if(loading)
			return <Loader />;
		if(errorLoadingData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
			/>;
		
		return <ListStyles.DivFlexMaxWidth ref={divRef}>
			<TableOfIncomeValues 
				data={dataTable as IDataTable[]}
				memorizedUrlParams={memorizedUrlParams.current}
				baseVerticalValues={baseVerticalValues.current}
				valuesOfQuantity={valuesOfQuantity.current}
				setLoading={(val: boolean) => setLoadingBackdrop(val)}
				showForecast={configs.forecast === "S"}
				showOnlyAccumulated={configs.showPeriods === "A"}
				showColumnLastYear={configs.showColumnLastYear === "S"}
				showColumnPlanned={configs.showColumnPlanned === "S"}
				showColumnRealized={configs.showColumnRealized === "S"}
				showVariationValue={configs.showVariationValue === "S"}
				showVariationPercent={configs.showVariationPercent === "S"}
				showHeader
				showHorizontalAnalysis={configs.showHorizontalAnalysis === "S"}
				showQuantity={configs.showQuantity === "S"}
				showVerticalAnalysis={configs.showVerticalAnalysis === "S"}
				title={`${translate("DRE")} - ${budgetPlanTitle}`}
				subsidiary={subsidiary?.title ?? subsidiaryDefaultLabel}
				openChartIndicators={(id: number, title: string | null) => {
					modalChartIndicatorsValues.current?.actionButtonOpenChartIndicators(id, title || "", new Date(configs.startDate).getFullYear().toString(), true, null, null);
				}}
				dtForecast={configs.dtForecast}
				coinPlannedInitials={configs.coinPlannedInitials !== null ? configs.coinPlannedInitials : undefined}
				coinRealizedInitials={configs.coinRealizedInitials !== null ? configs.coinRealizedInitials : undefined}
				onMouseDown={onMouseDown}
			/>
		</ListStyles.DivFlexMaxWidth>;

	}, [loading, errorLoadingData, dataTable]);

	useEffect(() => {
		fetchIfBudgetUsesApportionment();
	}, []);

	return (
		<>

			{/* Header -> Results + Buttons */}
			<PageTitle
				title={`${translate("Demonstrativo resultado do exercício")} - ${budgetPlanTitle}`}
				icon={{
					name: "dollar"
				}}
			>
				{corporativeValueOutdated && (
					<RCCTooltip
						text={translate("Valores corporativos desatualizados")}
						color="yellow"
					>
						<RCCButton
							icon={{name: "exclamationTriangle", color: "#FFF"}}
							color="yellow"
							layout="isRounded"
							size="md"
							onClick={updateCorporativeValues}
						/>
					</RCCTooltip>
				)}
				<RCCTooltip
					text={translate("Configurações")}
					color="blue"
				>
					<RCCButton
						icon={{name: "settings", color: "#FFF", size: 16}}
						color="blue"
						layout="isRounded"
						size="md"
						onClick={() => setShowConfigs((prevState) => !prevState)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Voltar")}
					color="grey"
				>
					<RCCButton
						icon={{name: "arrowBack", color: "#FFF"}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => history.push(`/financialmanagement/panel/${budgetPlanId}`)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Configs */}
			<ConfigsContainer
				showConfigs={showConfigs}
				showClearConfigs={false}
				showSubmitConfigs={false}
			>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="showPeriods"
							control={controlConfigs}
							defaultValue={configs.showPeriods}
							options={[{
								label: translate("Todos"),
								value: "T"
							},{
								label: translate("Acumulado"),
								value: "A"
							}]}
							label={translate("Mostrar períodos")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, showPeriods: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="divider"
							control={controlConfigs}
							defaultValue={configs.divider}
							options={[{
								label: "1",
								value: "1"
							},{
								label: "1000",
								value: "2"
							},{
								label: "1000000",
								value: "3"
							}]}
							label={translate("Divisor dos valores")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, divider: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="updateIndicators"
							control={controlConfigs}
							defaultValue={configs.updateIndicators}
							options={[{
								label: translate("Não"),
								value: "N"
							},{
								label: translate("Sim"),
								value: "S"
							}]}
							label={translate("Atualizar indicadores")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, updateIndicators: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="variation"
							control={controlConfigs}
							defaultValue={configs.variation}
							options={[{
								label: translate("Planejado x Realizado"),
								value: "PR"
							},{
								label: translate("Realizado x Ano anterior"),
								value: "RL"
							}]}
							label={translate("Variação")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, variation: e.value }))}
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
					alignItems="center"
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="forecast"
							control={controlConfigs}
							defaultValue={configs.forecast}
							options={[{
								label: translate("Não"),
								value: "N"
							},{
								label: translate("Sim"),
								value: "S"
							}]}
							label="Forecast"
							onChangeCapture={(e) => {
								setShowDtForecast(e.value === "S");
								setConfigs((prevState) => ({ ...prevState, forecast: e.value }));
							}}
						/>
					</RCCGrid>

					{showDtForecast && (
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={3}
							lg={3}
						>
							<DateTimePicker 
								type="datePicker"
								name="dtForecast"
								control={controlConfigs}
								defaultValue={configs.dtForecast && configs.dtForecast !== null ? 
									stringToDate(configs.dtForecast) : 
									new Date(new Date(new Date().getFullYear(), 0, 1))
								}
								label={`${translate("Data")} forecast`}
								props={{
									datePicker: {
										format: "MM/yyyy",
										onAccept: (date: any) => {
											setConfigs((prevState) => ({ ...prevState, dtForecast: date ? formatDate(date) : "" }));
										},
										onBlur: (e: any) => {
											if(e.target.value.length < 7)
												return;
											const value = `01/${e.target.value}`; 
											setConfigs((prevState) => ({ ...prevState, dtForecast: value }));
										},
										views: ["year", "month"]
									}
								}}
							/>
						</RCCGrid>
					)}

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="corporativeValues"
							control={controlConfigs}
							defaultValue={configs.corporativeValues}
							options={[{
								label: translate("Com rateio"),
								value: "N"
							},{
								label: translate("Sem rateio"),
								value: "S"
							}]}
							label={translate("Valores corporativos")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, corporativeValues: e.value }))}
						/>
					</RCCGrid>
					{coinConversion && (
						<>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
							>
								<LovCoins 
									name="coinPlanned"
									label={translate("Moeda planejado")}
									control={controlConfigs}
									defaultValue={configs.coinPlanned !== null ? configs.coinPlanned.toString() : undefined}
									isClearable
									onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
										...prevState, 
										coinPlanned: e != null ? e.value : null,
										coinPlannedInitials: e != null ? e.extendedProps.moa001Sigla : null  
									}))}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
							>
								<LovCoins 
									name="coinRealized"
									label={translate("Moeda realizado")}
									control={controlConfigs}
									defaultValue={configs.coinRealized !== null ? configs.coinRealized.toString() : undefined}
									isClearable
									onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
										...prevState, 
										coinRealized: e != null ? e.value : null,
										coinRealizedInitials: e != null ? e.extendedProps.moa001Sigla : null 
									}))}
								/>
							</RCCGrid>
						</>
					)}
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>

					<RCCGrid
						container
						item
						direction="column"
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Title color="purple">
							{translate("Coluna")}
						</Title>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnPlanned" 
								label={translate("Planejado")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnPlanned === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnPlanned: e.target.checked ? "S" : "N" }))}
								disabled={showDtForecast}
							/>
						</RCCGrid>
                        
						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnRealized" 
								label={translate("Realizado")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnRealized === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnRealized: e.target.checked ? "S" : "N" }))}
								disabled={showDtForecast}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnLastYear" 
								label={translate("Ano anterior")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnLastYear === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnLastYear: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showQuantity" 
								label={`${translate("Valor")}/${translate("Qtde")}`} 
								register={registerConfigs} 
								defaultChecked={configs.showQuantity === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showQuantity: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>
                        
					</RCCGrid>

					<RCCGrid
						container
						item
						direction="column"
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Title color="purple">
							{translate("Variação")}
						</Title>
						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showVariationValue" 
								label={`${translate("Variação")} $`} 
								register={registerConfigs} 
								defaultChecked={configs.showVariationValue === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showVariationValue: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showVariationPercent" 
								label={`${translate("Variação")} %`} 
								register={registerConfigs} 
								defaultChecked={configs.showVariationPercent === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showVariationPercent: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

					</RCCGrid>

					<RCCGrid
						container
						item
						direction="column"
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Title color="purple">
							{translate("Análise")}
						</Title>
						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showHorizontalAnalysis" 
								label={`${translate("Análise")} ${translate("horizontal")}`} 
								register={registerConfigs} 
								defaultChecked={configs.showHorizontalAnalysis === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showHorizontalAnalysis: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showVerticalAnalysis" 
								label={`${translate("Análise")} ${translate("vertical")}`} 
								register={registerConfigs} 
								defaultChecked={configs.showVerticalAnalysis === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showVerticalAnalysis: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

					</RCCGrid>

				</RCCGrid>
                       
			</ConfigsContainer>
			
			{/* Groupers Select -> Origin -> Delayed */}
			<form onSubmit={handleSubmitFormOptions(submitFormOptions)} style={{marginBottom: "1rem"}}>
				<RCCGrid
					container 
					item
					spacing={1}
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={12}
						md={4}
						lg={4}
					>
						<LovIncomeStatementWithOptions 
							control={controlFormOptions}
							budgetPlanId={Number(budgetPlanId)}
							setValue={setValueFormOptions}
						/>
					</RCCGrid>
				</RCCGrid>
				
				<RCCGrid
					container
					item
					spacing={1}
				>

					<RCCGrid
						container
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovSubsidiary 
							control={controlFormOptions}
							name="subsidiary"
							label={translate("Unidade/Filial")}
							defaultValue={subsidiaryDefaultValue && subsidiaryDefaultLabel ? {
								value: subsidiaryDefaultValue,
								label: subsidiaryDefaultLabel
							} : subsidiaryDefaultValue}
							extraParametersOnLoad={{
								budgetPlanId: Number(budgetPlanId)
							}}
							onChangeCapture={(e: any) => {setSubsidiary({
								title: e.label,
								id: Number(e.value)
							});
							}}
							setValue={setValueFormOptions}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<DateTimePickerRange
							control={controlFormOptions}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit = "startDate"
							nameEnd = "endDate"
							defaultValueEnd={configs.endDate}
							defaultValueInit={configs.startDate}
							type="datePicker"
							setValue={setValueFormOptions}
							periods={[
								{ name:translate("Ano atual") , initialDate: new Date(new Date().getFullYear(), 0, 1), endDate: subDays(new Date(new Date().getFullYear() + 1, 0, 1), 1) },
								{ name:translate("Ano anterior") , initialDate: new Date(new Date().getFullYear() - 1, 0, 1), endDate: subDays(new Date(new Date().getFullYear(), 0, 1), 1) },
								{ name:translate("Mês atual") , initialDate:startOfMonth(new Date()), endDate: endOfMonth(new Date()) },
								{ name:translate("Mês passado") , initialDate:startOfMonth(subMonths(new Date(),1)), endDate: endOfMonth(subMonths(new Date(),1)) }
							]}
							props={{
								datePicker: {
									format: "MM/yyyy",
									views: ["year", "month"]
								}
							}}
						/>
					</RCCGrid>
				
					<RCCGrid container item spacing={1} justifyContent="flex-end">
						{hasConversionValueEqualZero && (
							<RCCGrid container item alignItems="center" style={{ flex: 1 }}>
								<RCCIcon name="exclamationTriangle" color="var(--yellow)" />
								<Title color="defaultColor" size="xs">
									{translate("Existem períodos em que não foi cadastrado a conversão da moeda")}
								</Title>
							</RCCGrid>
						)}
						{dataTable && dataTable.length > 0 && (
							<RCCTooltip
								text={translate("Exportar")}
								color="purple"
							>
								<RCCButton
									color="purple"
									icon={{
										name: "download"
									}}
									layout="isRounded"
									size="md"
									loadingText=""
									loading={loadingExport}
									onClick={exportStructure}
								/>
							</RCCTooltip>
						)}
						<RCCTooltip
							text={translate("Consultar")}
							color="purple"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "search"
								}}
								layout="isRounded"
								size="md"
								type="submit"
								loadingText=""
								loading={loading}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
			</form>				
			
			<ExportListContainer
				ref={exportListContainerRef}
				classToBreakLineInsideSameColumn="query-budget-panel>div>div"
				style={{ position: "relative", zIndex: 0 }}
			>
				{Table}
			</ExportListContainer>

			<ButtonScrollTo
				minHeightToShowButton={400}
			/>

			<ModalIndicatorChart ref={modalChartIndicatorsValues} 
				openValuesOfPeriodIndicator={() => {}}
				onCloseCallback={() => {}}
			/>

			{loadingBackdrop && (
				<RCCLoaderBackdropComponent 
					transparent
				/>
			)}
		</>
	);
};

export default IncomeStatement;
